// The final-blow recipes, mockup round 053 (R1007).
//
// One entry per look id of `labels.toml hit_sink_styles`, keyed by the id. The
// layer picks this list when a fleet goes down (R1009). The rules of the file
// are the rules of `combat-recipes-melee.ts`.
//
// **A SINK LOOK OWNS THE VICTIM'S HULL.** `OWNS_HULL` names every id that
// draws the hull itself, and the layer then takes the fleet layer's own hull
// instance away for the life of the look. That is the map's answer to
// `HK.erase` of the mockup pages: the cards paint the harness hull out in the
// water ink, because the harness had already drawn it, and on the map the
// instance is simply not submitted. The overpaint has no twin here, and it must
// not get one: it would read wrong over a claim fill and over a hex grid, and
// the mockup harness had to sample a ground pixel to guess its ink.
//
// **A SINK LOOK LEAVES NO MARK.** Three cards flash the wreck glyph while the
// descent runs. The lasting wreck on the sea bed belongs to the wreck layer
// (R801 to R805), and a second mark on one site would read as two ships.

import {
  TAU, COMBAT_WHITE, DEEP_INK, FOAM_INK, NEUTRAL_INK, PALE_INK,
  clamp01, disc, flare, fog, hash, hull, hump, inn, line, out,
  polyline, ring, stroke, tail, wreck,
  type HitCtx, type HitRecipeFn, type HullDrawOpts, type HullMark, type Pt,
} from './combat-recipe-kit';

// ---------------------------------------------------------------------------
// The glyph figures every card of the round shares
// ---------------------------------------------------------------------------

/**
 * The wreck glyph as the sea chart draws it (R801): 0.6 of the card, the pale
 * ink at 0.35, one stroke of 0.9 world px.
 *
 * **A sink look leaves no mark.** Cards 01, 05 and 06 print the glyph and hold
 * it to the end of the card; here it fades back out over the last
 * `WRECK_HAND_AT` of the life, because the lasting wreck on the sea bed is the
 * wreck layer's alone (R801 to R805, R1007) and a second mark on one site
 * would read as two ships.
 */
const WRECK_ALPHA = 0.35, WRECK_SCALE = 0.6, WRECK_WIDTH_PX = 0.9;
const WRECK_HAND_AT = 0.85;

/** The transient glyph, gone before the look is. It draws nothing at alpha 0. */
function wreckFlash(
  c: HitCtx, x: number, y: number, modern: boolean, ink: number, a: number,
): void {
  const al = stroke(a * tail(c.p, WRECK_HAND_AT)) * c.alpha;
  if (al <= 0) return;
  wreck(c.segs, x, y, modern, ink, al, WRECK_WIDTH_PX, WRECK_SCALE);
}

/**
 * The bow end and the stern end of the hull in ITS OWN x, in world px.
 *
 * The cards write the Cutlass's own 14 and −8 (`HK.hullPath`), and the baked
 * cell answers the two for whichever class went down, so a Lancer breaks at
 * her own bow and not at a Cutlass's.
 */
function bowX(h: HullMark): number {
  const e = h.sprite(false);
  return e.ox + e.w / 2;
}

function sternX(h: HullMark): number {
  const e = h.sprite(false);
  return e.ox - e.w / 2;
}

/** The three option packs a hull draw takes, reused every frame (R131). */
const HALO_OPTS: HullDrawOpts = { halo: true };
const HOLLOW_OPTS: HullDrawOpts = { hollow: true };
const CLIP_OPTS: HullDrawOpts = {};

// ---------------------------------------------------------------------------
// The shared descent (`HK.descend` and `HK.leak` of round 053)
// ---------------------------------------------------------------------------

/** How far a hull shrinks over the whole fall, as a share of its size. */
const DESCEND_SHRINK = 0.65;
/** The faction ink's alpha at the top of the fall. */
const DESCEND_INK_ALPHA = 0.95;
/** How dark the deep ink humps over the fall. */
const DESCEND_DEEP_ALPHA = 0.7;
/** How bright a bubble of a leak stands. `HK.leak` fixes it for all twenty
 *  cards of the round, so it is the kit's own figure and not a row's. */
const LEAK_FOAM_ALPHA = 0.7;
/** A bubble's smallest radius and the span above it, in world px. */
const LEAK_R_MIN = 0.6, LEAK_R_SPAN = 1.2;
/** How far a bubble wanders off its lane, in world px, and how fast. */
const LEAK_WOBBLE_PX = 2, LEAK_WOBBLE_RATE = 6;
/** How much of the lane a bubble has crossed before it spreads. */
const LEAK_LANE_REST = 0.6;

/** The descent's own option pack, reused every frame (R131). */
const DESCEND_OPTS: HullDrawOpts = { halo: true };

/**
 * The plain descent, `u` in 0 to 1: the hull shrinks, goes to the deep ink and
 * fades. It is whole at `u <= 0` and gone at `u >= 1`. It is `HK.descend`.
 *
 * `sx`/`sy` are the card's own axial and beam scales, which the shrink
 * multiplies; `clipX0`/`clipX1` keep one slice of the hull in its own x.
 */
export function descend(
  c: HitCtx, h: HullMark, x: number, y: number, head: number, ink: number,
  u: number, sx = 1, sy = 1, clipX0?: number, clipX1?: number,
): void {
  const v = clamp01(u);
  if (v >= 1) return;
  const s = 1 - DESCEND_SHRINK * v;
  const kx = s * sx, ky = s * sy;
  // One reused pack: a recipe runs once a mark a frame and allocates nothing
  // (R131). The kit reads the pack and keeps no reference.
  DESCEND_OPTS.clipX0 = clipX0;
  DESCEND_OPTS.clipX1 = clipX1;
  hull(c, h, x, y, head, kx, ky, ink, DESCEND_INK_ALPHA * (1 - inn(v)), DESCEND_OPTS);
  hull(
    c, h, x, y, head, kx, ky, DEEP_INK,
    DESCEND_DEEP_ALPHA * hump(v), DESCEND_OPTS,
  );
}

/**
 * A leak: `n` bubbles on their own clocks rise `rise` px out of a lane
 * `spread` px wide. `gate` closes the source. It is `HK.leak`.
 */
export function leak(
  c: HitCtx, x: number, y: number, n: number, seed: number,
  u: number, gate: number, spread: number, rise: number,
): void {
  if (gate <= 0) return;
  for (let i = 0; i < n; i++) {
    const h0 = hash(seed, i * 3 + 1);
    const h1 = hash(seed, i * 3 + 2);
    const h2 = hash(seed, i * 3 + 3);
    const age = (u * 2 + h0) % 1;
    if (age > u * 2) continue;
    disc(
      c,
      x + (h1 - 0.5) * spread * (LEAK_LANE_REST + age)
        + Math.sin(age * LEAK_WOBBLE_RATE + h2 * 5) * LEAK_WOBBLE_PX,
      y - age * rise,
      LEAK_R_MIN + LEAK_R_SPAN * h2,
      FOAM_INK,
      LEAK_FOAM_ALPHA * hump(age) * gate,
    );
  }
}

// ---------------------------------------------------------------------------
// The proportions of card 15
// ---------------------------------------------------------------------------

/** spiral-descent, card 15: how much of the life the fall takes. The last
 *  8 % is the hull gone and the bubbles still rising. */
const SPIRAL_SPAN = 0.92;
/** spiral-descent: how many turns the spiral runs, and how the radius
 *  tightens to the site over them. */
const SPIRAL_TURNS = 1.1, SPIRAL_TIGHTEN = 0.5;
/** spiral-descent: how fast the radius opens at the start. */
const SPIRAL_OPEN = 1.2;
/** spiral-descent: how flat the spiral lies, so it reads as a fall and not
 *  as a circle on the surface. */
const SPIRAL_SQUASH = 0.7;
/** spiral-descent: how much of one turn the thread marks, and its steps. */
const SPIRAL_THREAD_SPAN = 0.25, SPIRAL_THREAD_STEPS = 12;
/** spiral-descent: where the bubbles stop, and over how long. */
const SPIRAL_GATE_AT = 0.6, SPIRAL_GATE_SPAN = 0.3;

/** The thread's own scratch, reused every frame (R131). */
const THREAD_X = new Float64Array(SPIRAL_THREAD_STEPS + 1);
const THREAD_Y = new Float64Array(SPIRAL_THREAD_STEPS + 1);
/** The one point of the spiral a step asks for, reused every frame. */
const SPIRAL_PT: Pt = { x: 0, y: 0 };

/**
 * The point of the spiral at fraction `v` of the fall, written into `out`:
 * one turn that opens fast and tightens to the site, lying flat. It is a
 * module function and not a closure of the recipe, so a frame allocates
 * nothing (R131).
 */
function spiralAt(c: HitCtx, v: number, out: Pt): Pt {
  const w = clamp01(v);
  const rad = c.row.rPx * hump(Math.min(1, w * SPIRAL_OPEN)) * (1 - w * SPIRAL_TIGHTEN);
  out.x = c.at.x + Math.cos(w * TAU * SPIRAL_TURNS) * rad;
  out.y = c.at.y + Math.sin(w * TAU * SPIRAL_TURNS) * rad * SPIRAL_SQUASH;
  return out;
}

// ---------------------------------------------------------------------------
// The proportions of the nine other cards
//
// Every FIGURE of a card stands in `data/presentation/combat.toml`. What stays
// here is what a figure is not: a share of the life, a share of a length, the
// shape of a fade — the rule of the effect recipes and of card 15 above.
// ---------------------------------------------------------------------------

/** bubble-collapse, card 01: when the hull starts to fall, and for how long. */
const BUBBLE_FALL_AT = 0.12, BUBBLE_FALL_SPAN = 0.68;
/** card 01: how much of the life the detonation bubble's rebound takes. */
const BUBBLE_SPAN = 0.16;
/**
 * card 01: the rebound itself — the share of the bubble's own life, and the
 * share of `r_px` the bubble stands at there. It grows, collapses, rebounds
 * to three fifths and goes (047 card 24).
 */
const BUBBLE_KF_AT = Float64Array.of(0, 0.12, 0.3, 0.42, 0.55, 0.62);
const BUBBLE_KF_R = Float64Array.of(0, 1, 0.32, 0.61, 0.21, 0);
/** card 01: the core inside the bubble, as a share of it, and its alpha. */
const BUBBLE_CORE_SHARE = 0.6, BUBBLE_CORE_ALPHA = 0.8;
/** card 01: how much of the life the shock ring takes, and how bright it is. */
const BUBBLE_SHOCK_SPAN = 0.1, BUBBLE_SHOCK_ALPHA = 0.5;
/** card 01: when the nine bubbles leave, over how long, and how much of that
 *  a bubble leads or waits. */
const BUBBLE_LEAK_AT = 0.1, BUBBLE_LEAK_SPAN = 0.6;
const BUBBLE_LEAK_LEAD = 1.3, BUBBLE_LEAK_STAGGER = 0.3;
/** card 01: a bubble's radius in world px, its wobble, and its alpha. */
const BUBBLE_R_MIN = 1, BUBBLE_R_SPAN = 2;
const BUBBLE_WOBBLE_PX = 3, BUBBLE_FOAM_ALPHA = 0.8;
/** card 01: when the wreck glyph starts to print, and over how long. */
const BUBBLE_WRECK_AT = 0.7, BUBBLE_WRECK_SPAN = 0.12;

/** break-in-two, card 02: how much of the life the two halves take to part. */
const SPLIT_SPAN = 0.7;
/** card 02: how far the stern half goes, against the bow half's own drift. */
const SPLIT_STERN_SHARE = 0.71;
/** card 02: the radians each half turns as it parts. The stern turns away. */
const SPLIT_STERN_TURN = -0.5, SPLIT_BOW_TURN = 0.35;
/** card 02: when each half starts to fall, and over how long. */
const SPLIT_STERN_AT = 0.06, SPLIT_STERN_LIFE = 0.6;
const SPLIT_BOW_AT = 0.2, SPLIT_BOW_LIFE = 0.65;
/** card 02: how much of the life the crack flashes for. */
const SPLIT_CRACK_SPAN = 0.12;
/** card 02: where the bubbles stop, and over how long. */
const SPLIT_GATE_AT = 0.6, SPLIT_GATE_SPAN = 0.3;
/** card 02: where the keel gives, in the hull's own x. It is the card's own
 *  split point, which is the hull cell's own origin. */
const SPLIT_AT_X = 0;

/** stern-first, card 03: how much of the life the flooding takes, and how
 *  much of her length the hull loses over it. */
const STERN_FLOOD_SPAN = 0.55, STERN_SHORTEN = 0.75;
/** card 03: when the bow follows, and over how long. */
const STERN_GO_AT = 0.5, STERN_GO_SPAN = 0.42;
/** card 03: when the pale bow tip starts to go, and over how long. */
const STERN_PALE_AT = 0.5, STERN_PALE_SPAN = 0.3;
/** card 03: where the bubbles stop, and over how long. */
const STERN_GATE_AT = 0.55, STERN_GATE_SPAN = 0.3;

/** silt-burst, card 05: how much of the life the hull's own fall takes. */
const SILT_FALL_SPAN = 0.32;
/** card 05: when the sea bed answers, and over how long. */
const SILT_AT = 0.3, SILT_SPAN = 0.7;
/** card 05: how long the cloud holds before it thins, as a share of its own
 *  life; how flat it lies; and the core inside it, as a share of it. */
const SILT_HOLD_FROM = 0.35, SILT_FLAT = 0.6, SILT_CORE = 0.5;
/** card 05: how far under the site the cloud sits, in world px. */
const SILT_DROP_PX = 2;
/** card 05: a grain's radius in world px, its alpha, and how much of the
 *  cloud's life a grain waits. */
const SILT_R_MIN = 0.8, SILT_R_SPAN = 1;
const SILT_GRAIN_ALPHA = 0.7, SILT_GRAIN_STAGGER = 0.3, SILT_GRAIN_SPAN = 0.7;
/** card 05: when the wreck glyph starts to print, and over how long. */
const SILT_WRECK_AT = 0.6, SILT_WRECK_SPAN = 0.15;

/** wreck-print, card 06: how much of the life the quiet fall takes. */
const PRINT_FALL_SPAN = 0.62;
/** card 06: where the bubbles stop, and over how long. */
const PRINT_GATE_AT = 0.45, PRINT_GATE_SPAN = 0.2;
/** card 06: when the mark starts to print, and over how long. */
const PRINT_AT = 0.5, PRINT_SPAN = 0.2;

/** last-light, card 09: how much of the life the fall takes, and how much of
 *  her size the hull loses over it. */
const LIGHT_SPAN = 0.95, LIGHT_SHRINK = 0.55;
/** card 09: the faction ink and the deep ink over the fall, and how fast the
 *  deep ink comes up under the fade. */
const LIGHT_INK_ALPHA = 0.95, LIGHT_DEEP_ALPHA = 0.85, LIGHT_DEEP_LEAD = 1.6;
/** card 09: how much of the blink rate the last blink has lost. */
const LIGHT_SLOWING = 0.4;

/** implosion, card 10: how much of the life the hull's own fall takes. */
const CRUSH_FALL_SPAN = 0.45;
/** card 10: when the water closes, and over how long. */
const CRUSH_AT = 0.25, CRUSH_SPAN = 0.25;
/** card 10: the inbound ring's alpha where it starts and where it arrives. */
const CRUSH_RING_ALPHA0 = 0.3, CRUSH_RING_ALPHA1 = 0.6;
/** card 10: how far the pale ring stands outside the white one, in world px,
 *  and how bright it is at the moment of the collapse. */
const CRUSH_PALE_PX = 5, CRUSH_PALE_ALPHA = 0.3;
/** card 10: when the collapse marks itself, and over how long. */
const CRUSH_FLASH_AT = 0.5, CRUSH_FLASH_SPAN = 0.35;
/** card 10: the pulse's first radius in world px. */
const CRUSH_PULSE_R0 = 3;
/** card 10: when the bubbles leave, where they stop, and over how long. */
const CRUSH_LEAK_AT = 0.5, CRUSH_LEAK_SPAN = 0.5;
const CRUSH_GATE_AT = 0.8, CRUSH_GATE_SPAN = 0.2;

/** ink-ghost, card 11: how much of the life the fill takes to drain. */
const GHOST_DRAIN_SPAN = 0.2;
/** card 11: when the outline leaves, and over how long. */
const GHOST_LEAVE_AT = 0.15, GHOST_LEAVE_SPAN = 0.85;
/** card 11: the fill's alpha while it drains. */
const GHOST_FILL_ALPHA = 0.95;
/** card 11: how much the outline swells over its drift, and how the second
 *  outline stands wider than the first. */
const GHOST_SWELL = 0.3, GHOST_WIDE_X = 1.15, GHOST_WIDE_Y = 1.3;
/** card 11: how often the drift wanders and how often the outline turns, over
 *  the whole drift. */
const GHOST_WOBBLE_RATE = 5, GHOST_TURN_RATE = 4;

/** steam-shroud, card 12: when the hull starts to fall, and for how long. */
const SHROUD_FALL_AT = 0.1, SHROUD_FALL_SPAN = 0.7;
/** card 12: how long the glow under the shroud holds before it thins. */
const SHROUD_GLOW_FROM = 0.4;
/** card 12: when the lane closes, how much of the life a bubble of the bow
 *  end waits for that, and over how long a bubble stops. The card's own
 *  spread is 0.25, which closes the last lane a beat AFTER the life is over;
 *  0.18 closes it with the life, so the look leaves nothing behind. */
const SHROUD_CLOSE_AT = 0.7, SHROUD_CLOSE_SPREAD = 0.18, SHROUD_CLOSE_SPAN = 0.12;
/** card 12: how far a bubble wanders across its lane, in world px, how much
 *  of that it carries from the start, and which hash row it reads. */
const SHROUD_WOBBLE_PX = 5, SHROUD_WOBBLE_REST = 0.5, SHROUD_WOBBLE_ROW = 7;
/** card 12: a bubble's radius in world px. */
const SHROUD_R_MIN = 0.5, SHROUD_R_SPAN = 0.8;

export const RECIPES: Readonly<Record<string, HitRecipeFn>> = {
  /**
   * Card 15, SPIRAL DESCENT.
   *
   * The hull turns as it falls, the way a leaf falls. Its centre runs one turn
   * of a spiral that tightens to the site, its heading turns with it, it
   * shrinks and fades, and a pale thread marks the last quarter turn of its
   * track.
   */
  'spiral-descent'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row;
    const u = clamp01(c.p / SPIRAL_SPAN);
    for (let i = 0; i <= SPIRAL_THREAD_STEPS; i++) {
      const v = u - SPIRAL_THREAD_SPAN + (i * SPIRAL_THREAD_SPAN) / SPIRAL_THREAD_STEPS;
      const q = spiralAt(c, v, SPIRAL_PT);
      THREAD_X[i] = q.x;
      THREAD_Y[i] = q.y;
    }
    polyline(
      c, THREAD_X, THREAD_Y, SPIRAL_THREAD_STEPS + 1,
      r.widthPx, PALE_INK, r.alpha2 * (1 - u),
    );
    const s = spiralAt(c, u, SPIRAL_PT);
    const hx = s.x, hy = s.y;
    descend(c, h, hx, hy, h.head + r.speed * out(u), h.ink, u);
    const gate = 1 - clamp01((c.p - SPIRAL_GATE_AT) / SPIRAL_GATE_SPAN);
    leak(c, hx, hy, r.count, c.seed, c.p, gate, r.lengthPx, r.length2Px);
  },

  /**
   * Card 01, BUBBLE COLLAPSE (from 047 card 24).
   *
   * The detonation bubble grows, collapses and rebounds over the hull in the
   * first half second, a shock ring leaves the site, and the hull goes down
   * under it while nine bubbles rise off it. The wreck glyph prints at the
   * end and hands the site back (`WRECK_HAND_AT`).
   */
  'bubble-collapse'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    descend(
      c, h, at.x, at.y, h.head, h.ink,
      (c.p - BUBBLE_FALL_AT) / BUBBLE_FALL_SPAN,
    );
    // The rebound, read off the keyframes with the card's own ease in and out.
    const q = c.p / BUBBLE_SPAN;
    let rad = 0;
    for (let i = 1; i < BUBBLE_KF_AT.length; i++) {
      if (q > BUBBLE_KF_AT[i]) continue;
      const u = (q - BUBBLE_KF_AT[i - 1]) / (BUBBLE_KF_AT[i] - BUBBLE_KF_AT[i - 1]);
      const e = u < 0.5 ? 2 * u * u : 1 - 2 * (1 - u) * (1 - u);
      rad = (BUBBLE_KF_R[i - 1] + (BUBBLE_KF_R[i] - BUBBLE_KF_R[i - 1]) * e) * r.rPx;
      break;
    }
    if (rad > 0) {
      disc(c, at.x, at.y, rad, COMBAT_WHITE, r.alpha2);
      ring(c, at.x, at.y, rad, r.widthPx, COMBAT_WHITE, r.alpha);
      flare(
        c, at.x, at.y, rad * BUBBLE_CORE_SHARE, COMBAT_WHITE,
        BUBBLE_CORE_ALPHA * (1 - q),
      );
    }
    const s = c.p / BUBBLE_SHOCK_SPAN;
    ring(
      c, at.x, at.y, r.r2Px + r.driftPx * out(s), r.width2Px, PALE_INK,
      BUBBLE_SHOCK_ALPHA * (1 - s),
    );
    const u = clamp01((c.p - BUBBLE_LEAK_AT) / BUBBLE_LEAK_SPAN);
    for (let i = 0; i < r.count; i++) {
      const h1 = hash(c.seed, i * 2 + 1), h2 = hash(c.seed, i * 2 + 2);
      const age = clamp01(u * BUBBLE_LEAK_LEAD - h1 * BUBBLE_LEAK_STAGGER);
      if (age <= 0) continue;
      disc(
        c,
        at.x + (h2 - 0.5) * r.lengthPx
          + Math.sin(age * LEAK_WOBBLE_RATE + h1 * 5) * BUBBLE_WOBBLE_PX,
        at.y - age * r.length2Px,
        BUBBLE_R_MIN + BUBBLE_R_SPAN * h1,
        FOAM_INK,
        BUBBLE_FOAM_ALPHA * (1 - age),
      );
    }
    wreckFlash(
      c, at.x, at.y, false, PALE_INK,
      WRECK_ALPHA * clamp01((c.p - BUBBLE_WRECK_AT) / BUBBLE_WRECK_SPAN),
    );
  },

  /**
   * Card 02, BREAK IN TWO.
   *
   * The keel gives at the hit. A white crack flashes across the hull, the
   * stern half turns away and goes down first, the bow half drifts on and
   * follows, and a thread of bubbles rises between the two.
   */
  'break-in-two'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    const head = h.head, ux = Math.cos(head), uy = Math.sin(head);
    const u = clamp01(c.p / SPLIT_SPAN);
    const aft = r.driftPx * SPLIT_STERN_SHARE * u, fwd = r.driftPx * u;
    // Two slices of one silhouette, in the hull's own x. The kit narrows the
    // hull cell alone, so the two halves share one unclipped halo, which the
    // card's own clip cut with the fill.
    descend(
      c, h, at.x - ux * aft, at.y - uy * aft, head + SPLIT_STERN_TURN * u, h.ink,
      (c.p - SPLIT_STERN_AT) / SPLIT_STERN_LIFE, 1, 1, undefined, SPLIT_AT_X,
    );
    descend(
      c, h, at.x + ux * fwd, at.y + uy * fwd, head + SPLIT_BOW_TURN * u, h.ink,
      (c.p - SPLIT_BOW_AT) / SPLIT_BOW_LIFE, 1, 1, SPLIT_AT_X, undefined,
    );
    const f = 1 - clamp01(c.p / SPLIT_CRACK_SPAN);
    const nx = -uy * r.r2Px, ny = ux * r.r2Px;
    line(
      c, at.x + nx, at.y + ny, at.x - nx, at.y - ny,
      r.widthPx, COMBAT_WHITE, r.alpha * f,
    );
    flare(c, at.x, at.y, r.rPx, COMBAT_WHITE, r.alpha2 * f);
    const gate = 1 - clamp01((c.p - SPLIT_GATE_AT) / SPLIT_GATE_SPAN);
    leak(c, at.x, at.y, r.count, c.seed, c.p, gate, r.lengthPx, r.length2Px);
  },

  /**
   * Card 03, STERN FIRST.
   *
   * The stern floods and goes under while the bow stands up out of the water:
   * the hull shortens toward her bow, the bow tip pales, bubbles pour off the
   * stern, and then the bow follows and the whole hull fades.
   */
  'stern-first'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    const head = h.head, ux = Math.cos(head), uy = Math.sin(head);
    const u1 = clamp01(c.p / STERN_FLOOD_SPAN);
    const sx = 1 - STERN_SHORTEN * inn(u1);
    // The bow point stays put while the hull shortens about her own centre.
    const bx = bowX(h), lost = bx * (1 - sx);
    const ox = at.x + ux * lost, oy = at.y + uy * lost;
    descend(
      c, h, ox, oy, head, h.ink, (c.p - STERN_GO_AT) / STERN_GO_SPAN, sx, 1,
    );
    // The tip out of the water. It takes no halo: the kit's halo is the whole
    // hull's and the clip does not narrow it, so it would glow astern too.
    CLIP_OPTS.clipX0 = bx - r.rPx;
    CLIP_OPTS.clipX1 = undefined;
    hull(
      c, h, ox, oy, head, sx, 1, PALE_INK,
      r.alpha2 * u1 * (1 - clamp01((c.p - STERN_PALE_AT) / STERN_PALE_SPAN)),
      CLIP_OPTS,
    );
    const stern = sternX(h) * sx;
    const gate = 1 - clamp01((c.p - STERN_GATE_AT) / STERN_GATE_SPAN);
    leak(
      c, ox + ux * stern, oy + uy * stern, r.count, c.seed, c.p, gate,
      r.lengthPx, r.length2Px,
    );
  },

  /**
   * Card 05, SILT BURST.
   *
   * The hull drops fast and the sea bed answers: a cloud of silt bursts out
   * from under the site, grains rise out of it, and the cloud hangs while the
   * wreck glyph prints inside it and hands the site back.
   */
  'silt-burst'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    descend(c, h, at.x, at.y, h.head, h.ink, c.p / SILT_FALL_SPAN);
    const u = clamp01((c.p - SILT_AT) / SILT_SPAN);
    const rad = r.rPx + r.r2Px * out(u);
    const f = tail(u, SILT_HOLD_FROM);
    const y = at.y + SILT_DROP_PX;
    fog(c, at.x, y, rad, rad * SILT_FLAT, 0, NEUTRAL_INK, r.alpha * f);
    fog(
      c, at.x, y, rad * SILT_CORE, rad * SILT_CORE * SILT_FLAT, 0,
      NEUTRAL_INK, r.alpha2 * f,
    );
    for (let i = 0; i < r.count; i++) {
      const h1 = hash(c.seed, i * 2 + 1), h2 = hash(c.seed, i * 2 + 2);
      const age = clamp01((u - h1 * SILT_GRAIN_STAGGER) / SILT_GRAIN_SPAN);
      // A grain of age 1 has settled. `hump` answers a rounding and not a
      // zero there, and a disc of that alpha is an instance that says nothing.
      if (age <= 0 || age >= 1) continue;
      disc(
        c, at.x + (h2 - 0.5) * r.lengthPx, at.y - age * r.length2Px,
        SILT_R_MIN + SILT_R_SPAN * h1, NEUTRAL_INK,
        SILT_GRAIN_ALPHA * hump(age),
      );
    }
    wreckFlash(
      c, at.x, at.y, false, PALE_INK,
      WRECK_ALPHA * clamp01((c.p - SILT_WRECK_AT) / SILT_WRECK_SPAN),
    );
  },

  /**
   * Card 06, WRECK PRINT.
   *
   * The quiet record. The hull shrinks, darkens and fades, and the chart's own
   * modern wreck symbol prints on the site with one white flash as she leaves.
   * The print then fades again: the lasting mark is the wreck layer's (R801).
   */
  'wreck-print'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    descend(c, h, at.x, at.y, h.head, h.ink, c.p / PRINT_FALL_SPAN);
    const gate = 1 - clamp01((c.p - PRINT_GATE_AT) / PRINT_GATE_SPAN);
    leak(c, at.x, at.y, r.count, c.seed, c.p, gate, r.lengthPx, r.length2Px);
    const w = clamp01((c.p - PRINT_AT) / PRINT_SPAN);
    wreckFlash(c, at.x, at.y, true, PALE_INK, WRECK_ALPHA * w);
    wreckFlash(
      c, at.x, at.y, true, COMBAT_WHITE,
      r.alpha * hump(w) * (1 - w),
    );
  },

  /**
   * Card 09, LAST LIGHT.
   *
   * No blast at all. The hull goes to the deep ink and shrinks, and one white
   * lamp at her centre blinks three times, each blink slower than the last,
   * and does not come back after the third.
   */
  'last-light'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    const u = clamp01(c.p / LIGHT_SPAN);
    const s = 1 - LIGHT_SHRINK * u;
    hull(
      c, h, at.x, at.y, h.head, s, s, h.ink,
      LIGHT_INK_ALPHA * (1 - inn(u)), HALO_OPTS,
    );
    hull(
      c, h, at.x, at.y, h.head, s, s, DEEP_INK,
      LIGHT_DEEP_ALPHA * clamp01(u * LIGHT_DEEP_LEAD) * (1 - inn(u)), HALO_OPTS,
    );
    const ph = r.speed * c.p * (1 - LIGHT_SLOWING * c.p);
    if (ph >= r.count) return;
    const b = Math.abs(Math.sin(Math.PI * ph));
    const k = b * b * b;
    flare(c, at.x, at.y, r.rPx * s, COMBAT_WHITE, r.alpha * k);
    disc(c, at.x, at.y, r.r2Px * s, COMBAT_WHITE, r.alpha2 * k);
  },

  /**
   * Card 10, IMPLOSION (the AUTO look, R1279).
   *
   * The hull drops under crush depth and the water closes on her. A white ring
   * runs INWARD to the site, a flash marks the collapse, one pale pulse runs
   * back out, and bubbles rise. The ring runs the other way to every other
   * ring of the pack, so it reads as its own event.
   */
  'implosion'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    descend(c, h, at.x, at.y, h.head, h.ink, c.p / CRUSH_FALL_SPAN);
    const u = clamp01((c.p - CRUSH_AT) / CRUSH_SPAN);
    if (u > 0 && u < 1) {
      const rad = r.rPx * (1 - inn(u));
      ring(
        c, at.x, at.y, rad, r.widthPx, COMBAT_WHITE,
        CRUSH_RING_ALPHA0 + CRUSH_RING_ALPHA1 * u,
      );
      ring(
        c, at.x, at.y, rad + CRUSH_PALE_PX, r.width2Px, PALE_INK,
        CRUSH_PALE_ALPHA * u,
      );
    }
    // The card writes `v > 0`, which drops the one frame the flash is
    // brightest at. The flash starts where the ring arrives.
    const v = clamp01((c.p - CRUSH_FLASH_AT) / CRUSH_FLASH_SPAN);
    if (c.p >= CRUSH_FLASH_AT) {
      flare(c, at.x, at.y, r.r2Px * (1 - v), COMBAT_WHITE, r.alpha * (1 - v));
      ring(
        c, at.x, at.y, CRUSH_PULSE_R0 + r.driftPx * out(v), r.width2Px, PALE_INK,
        r.alpha2 * (1 - v),
      );
    }
    const gate = 1 - clamp01((c.p - CRUSH_GATE_AT) / CRUSH_GATE_SPAN);
    leak(
      c, at.x, at.y, r.count, c.seed,
      clamp01((c.p - CRUSH_LEAK_AT) / CRUSH_LEAK_SPAN), gate,
      r.lengthPx, r.length2Px,
    );
  },

  /**
   * Card 11, INK GHOST.
   *
   * The fill drains out of the hull and leaves her outline in the faction ink.
   * The outline drifts off with the current, swells and thins to nothing.
   *
   * The card strokes the outline at two widths; the kit has one baked hollow
   * master, so the two outlines differ by their scale and their ink alone.
   */
  'ink-ghost'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    const u = clamp01(c.p / GHOST_DRAIN_SPAN);
    const v = clamp01((c.p - GHOST_LEAVE_AT) / GHOST_LEAVE_SPAN);
    hull(
      c, h, at.x, at.y, h.head, 1, 1, h.ink,
      GHOST_FILL_ALPHA * (1 - u), HALO_OPTS,
    );
    const x = at.x + r.lengthPx * v + Math.sin(v * GHOST_WOBBLE_RATE) * r.rPx;
    const y = at.y - r.length2Px * v;
    const s = 1 + GHOST_SWELL * v;
    const rot = h.head + r.speed * Math.sin(v * GHOST_TURN_RATE);
    hull(c, h, x, y, rot, s, s, h.ink, r.alpha * u * (1 - inn(v)), HOLLOW_OPTS);
    hull(
      c, h, x, y, rot, s * GHOST_WIDE_X, s * GHOST_WIDE_Y, h.ink,
      r.alpha2 * u * (1 - v), HOLLOW_OPTS,
    );
  },

  /**
   * Card 12, STEAM SHROUD (from 047 card 22).
   *
   * A column as wide as the hull. The bubbles climb out of a lane the length
   * of her while she goes down under them, and the lane closes from the stern
   * to the bow at the end. A white glow sits under the shroud.
   */
  'steam-shroud'(c) {
    const h = c.hull;
    if (!h) return;
    const r = c.row, at = c.at;
    descend(
      c, h, at.x, at.y, h.head, h.ink,
      (c.p - SHROUD_FALL_AT) / SHROUD_FALL_SPAN,
    );
    const ux = Math.cos(h.head), uy = Math.sin(h.head);
    flare(
      c, at.x, at.y, r.rPx, COMBAT_WHITE, r.alpha * tail(c.p, SHROUD_GLOW_FROM),
    );
    const lead = c.p * r.speed;
    for (let i = 0; i < r.count; i++) {
      const h0 = hash(c.seed, i * 2 + 1), h1 = hash(c.seed, i * 2 + 2);
      const age = (lead + h0) % 1;
      if (age > lead) continue;
      const along = (h1 - 0.5) * r.lengthPx;
      const gate = 1 - clamp01(
        (c.p - SHROUD_CLOSE_AT - h1 * SHROUD_CLOSE_SPREAD) / SHROUD_CLOSE_SPAN,
      );
      const wob = (hash(c.seed, i + SHROUD_WOBBLE_ROW) - 0.5) * SHROUD_WOBBLE_PX
        * (SHROUD_WOBBLE_REST + age);
      disc(
        c, at.x + ux * along + wob, at.y + uy * along - age * r.length2Px,
        SHROUD_R_MIN + SHROUD_R_SPAN * h1, FOAM_INK,
        r.alpha2 * hump(age) * gate,
      );
    }
  },
};

/**
 * Every id whose look draws the victim's hull itself.
 *
 * The layer reads it and takes the fleet layer's hull instance away for the
 * life of the look. All ten cards of round 053 do this, because a fleet that
 * went down must not stand on the water while its own descent plays.
 */
export const OWNS_HULL: readonly string[] = [
  'spiral-descent',
  'bubble-collapse',
  'break-in-two',
  'stern-first',
  'silt-burst',
  'wreck-print',
  'last-light',
  'implosion',
  'ink-ghost',
  'steam-shroud',
];

/**
 * The cards of round 053 that wait for their own recipe.
 *
 * The table is empty: all ten cards of the round draw their own look. The
 * mockup page `archive/mockups/053-hits-sinking.html` is the specification of
 * each, and `<file>.test.ts` refuses an id that stands in neither table.
 */
export const PENDING: readonly string[] = [];
